/**
 * Debounced autosave for editor commands. The editor calls `queue(plan)`
 * after every successful command; writes coalesce inside a 300 ms window
 * so a fast undo→redo→drag burst becomes a single Dexie update.
 *
 * `flush()` runs the pending write synchronously — call it from editor
 * unmount and `beforeunload` so the user never loses more than the in-flight
 * 300 ms window.
 */
import type { Plan } from '$lib/types'
import * as plansStore from '$lib/storage/plans'

export const AUTOSAVE_DEBOUNCE_MS = 300

export interface Autosave {
  queue(plan: Plan): void
  flush(): Promise<void>
  cancel(): void
}

type SaveFn = (planId: number, changes: Partial<Plan>) => Promise<void>

export interface AutosaveOptions {
  debounceMs?: number
  /** Override the storage write — primarily a test hook. */
  save?: SaveFn
}

export function createAutosave(planId: number, options: AutosaveOptions = {}): Autosave {
  const debounceMs = options.debounceMs ?? AUTOSAVE_DEBOUNCE_MS
  const save: SaveFn = options.save ?? ((id, changes) => plansStore.update(id, changes))

  let timer: ReturnType<typeof setTimeout> | null = null
  let pending: Plan | null = null
  let inflight: Promise<void> | null = null

  function snapshotChanges(plan: Plan): Partial<Plan> {
    // Only the editor-mutable surface is autosaved. `name` has its own write
    // path (rename) and the import-derived fields don't change here.
    return {
      buildings: plan.buildings,
      inventory: plan.inventory,
      nextEntityId: plan.nextEntityId,
    }
  }

  async function doSave(plan: Plan): Promise<void> {
    inflight = save(planId, snapshotChanges(plan)).finally(() => {
      inflight = null
    })
    return inflight
  }

  function scheduleFlush(): void {
    timer = setTimeout(() => {
      timer = null
      const toSave = pending
      pending = null
      if (toSave) void doSave(toSave)
    }, debounceMs)
  }

  return {
    queue(plan: Plan): void {
      pending = plan
      if (timer !== null) clearTimeout(timer)
      scheduleFlush()
    },
    async flush(): Promise<void> {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
      const toSave = pending
      pending = null
      if (toSave) await doSave(toSave)
      // Wait for any save we kicked off without a pending replacement.
      if (inflight) await inflight
    },
    cancel(): void {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
      pending = null
    },
  }
}
